import { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Wrench } from "lucide-react";
import { toast } from "sonner";
import {
  createDevelopmentCost,
  deleteDevelopmentCost,
  listDevelopmentCosts,
  currentMonthStart,
  COST_TYPE_LABELS,
  type AllocationBasis,
  type CostType,
  type DevelopmentCost,
} from "@/lib/developmentCosts";

const formatEur = (n: number) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: 2 }).format(n);
const formatCredits = (n: number) =>
  new Intl.NumberFormat("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n);
const monthLabel = (iso: string) => {
  const [y, m] = iso.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("es-ES", { month: "long", year: "numeric" });
};

const parseAmount = (v: string) => Number(v.replace(/\./g, "").replace(",", ".")) || 0;

const DevelopmentCostsCard = ({ onChanged }: { onChanged?: () => void }) => {
  const [costs, setCosts] = useState<DevelopmentCost[]>([]);
  const [saving, setSaving] = useState(false);
  const [month, setMonth] = useState(currentMonthStart().slice(0, 7));
  const [concept, setConcept] = useState("");
  const [amount, setAmount] = useState("");
  const [credits, setCredits] = useState("");
  const [basis, setBasis] = useState<AllocationBasis>("documents");
  const [costType, setCostType] = useState<CostType>("desarrollo");

  const load = async () => setCosts(await listDevelopmentCosts());

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async () => {
    const eur = parseAmount(amount);
    const cr = parseAmount(credits);
    if (!concept.trim() || (eur <= 0 && cr <= 0)) {
      toast.error("Indica un concepto y un importe o créditos");
      return;
    }
    setSaving(true);
    const ok = await createDevelopmentCost({
      month: `${month}-01`,
      concept: concept.trim(),
      amountEur: eur,
      credits: cr,
      allocationBasis: basis,
      costType,
    });
    setSaving(false);
    if (!ok) {
      toast.error("No se pudo guardar el coste");
      return;
    }
    setConcept("");
    setAmount("");
    setCredits("");
    setCostType("desarrollo");
    toast.success("Coste registrado");
    load();
    onChanged?.();
  };

  const handleDelete = async (id: string) => {
    const ok = await deleteDevelopmentCost(id);
    if (!ok) {
      toast.error("No se pudo eliminar");
      return;
    }
    load();
    onChanged?.();
  };

  return (
    <section>
      <div className="mb-4 flex items-center gap-2">
        <Wrench className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold text-primary">Costes de desarrollo, hosting y running</h2>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        Los créditos que se consumen al construir, alojar o ejecutar la aplicación se introducen aquí cada mes y se
        reparten entre las empresas según los documentos tramitados o los usuarios activos.
      </p>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Registrar coste</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-7">
            <div className="md:col-span-1">
              <Label className="text-xs">Mes</Label>
              <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Concepto</Label>
              <Input
                value={concept}
                onChange={(e) => setConcept(e.target.value)}
                placeholder="Ej.: factura Lovable marzo"
              />
            </div>
            <div>
              <Label className="text-xs">Importe (€)</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0,00"
              />
            </div>
            <div>
              <Label className="text-xs">Créditos</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={credits}
                onChange={(e) => setCredits(e.target.value)}
                placeholder="0"
              />
            </div>
            <div>
              <Label className="text-xs">Tipo</Label>
              <Select value={costType} onValueChange={(v) => setCostType(v as CostType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="desarrollo">{COST_TYPE_LABELS.desarrollo}</SelectItem>
                  <SelectItem value="hosting">{COST_TYPE_LABELS.hosting}</SelectItem>
                  <SelectItem value="running">{COST_TYPE_LABELS.running}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Reparto</Label>
              <Select value={basis} onValueChange={(v) => setBasis(v as AllocationBasis)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="documents">Por documentos</SelectItem>
                  <SelectItem value="users">Por usuarios activos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button onClick={handleAdd} disabled={saving}>
              <Plus className="mr-2 h-4 w-4" />
              Añadir coste
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Costes registrados</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Mes</TableHead>
                  <TableHead>Concepto</TableHead>
                  <TableHead className="text-right">Importe</TableHead>
                  <TableHead className="text-right">Créditos</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {costs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="p-8 text-center text-sm text-muted-foreground">
                      Aún no hay costes registrados.
                    </TableCell>
                  </TableRow>
                ) : (
                  costs.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="capitalize">{monthLabel(c.month)}</TableCell>
                      <TableCell>
                        <div>{c.concept}</div>
                        <div className="text-xs text-muted-foreground">
                          {c.allocationBasis === "users" ? "Reparto por usuarios" : "Reparto por documentos"}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono">{formatEur(c.amountEur)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCredits(c.credits)}</TableCell>
                      <TableCell>{COST_TYPE_LABELS[c.costType] ?? c.costType}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(c.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </section>
  );
};

export default DevelopmentCostsCard;
